

import { ReactNode, memo } from 'react';

import { ConsumptionTypeDto } from '@eneco/dc-be/client';
import { useFormatter } from '@eneco/i18n';
import { getTokens } from '@eneco/sparky/stitches';
import { useContent } from '@sitecore/common';
import { ConsumptionGraphicalOverviewRendering } from '@sitecore/types/ConsumptionGraphicalOverview';
import { SeriesPoint } from '@visx/shape/lib/types';
import { useTooltip, useTooltipInPortal, defaultStyles } from '@visx/tooltip';

const { colors } = getTokens();

const tooltipStyles = {
  ...defaultStyles,
  minWidth: 60,
  backgroundColor: colors.backgroundDark.toString(),
  color: colors.textInverted.toString(),
  borderRadius: 4,
  padding: 8,
};

type Data = Record<string, string | number | undefined>;

export type TooltipData = {
  bar: SeriesPoint<Data>;
  key: string;
  index: number;
  height: number;
  width: number;
  x: number;
  y: number;
  color: string;
};

interface TooltipContainerProps {
  consumptionType: ConsumptionTypeDto;
  children: (tooltipHandlers: {
    showTooltip: (args: { tooltipData: TooltipData; tooltipTop?: number; tooltipLeft?: number }) => void;
    hideTooltip: () => void;
    containerRef: (element: HTMLElement | SVGElement | null) => void;
  }) => ReactNode;
}

export const TooltipContainer = memo<TooltipContainerProps>(({ consumptionType, children }) => {
  const { fields } = useContent<ConsumptionGraphicalOverviewRendering>();
  const { unit } = useFormatter();
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, hideTooltip, showTooltip } = useTooltip<TooltipData>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
  });

  const hasDayNightElectricity =
    tooltipData?.bar.data.electricitySecondary !== undefined && tooltipData?.bar.data.electricity !== undefined;

  const hasDayNightInjection =
    tooltipData?.bar.data.injection !== undefined && tooltipData?.bar.data.injectionSecondary !== undefined;

  let tooltipTitle = '';
  const key = (tooltipData?.key ?? '').toLowerCase();

  const mapKeys = {
    electricity: hasDayNightElectricity ? 'electricityday' : 'electricity',
    electricitysecondary: hasDayNightElectricity ? 'electricitynight' : 'electricitysecondary',
    injection: hasDayNightInjection ? 'injectionday' : 'injection',
    injectionsecondary: hasDayNightInjection ? 'injectionnight' : 'injectionsecondary',
    value: 'peakvalues',
  } as Record<string, string>;

  const lookupKey = mapKeys[key] ?? key;
  const found = fields.data?.tooltipConsumptionOptionsList?.value?.enum?.find(
    c => c.value.toLowerCase() === lookupKey.toLocaleLowerCase(),
  );
  tooltipTitle = found ? found.label : '';

  const tooltipValue = Number(Number(tooltipData?.bar?.data?.[tooltipData?.key as keyof Data]).toFixed(2));
  const tooltipBody = consumptionType === 'PeakValues' ? `${tooltipValue} kW` : unit.electricity(tooltipValue);

  return (
    <>
      {children({ showTooltip, hideTooltip, containerRef })}
      {tooltipOpen && tooltipData && (
        // eslint-disable-next-line dxp-rules/no-inline-css
        <TooltipInPortal
          style={tooltipStyles}
          top={tooltipTop}
          left={tooltipLeft}
          key={`tooltip-${tooltipData.key}-${tooltipData.index}`}>
          <div>
            <p>{tooltipTitle}</p>
            <p>{tooltipBody}</p>
          </div>
        </TooltipInPortal>
      )}
    </>
  );
});
